/**
 * Types and helpers for error handling.
 *
 * @packageDocumentation
 */
import React from 'react';
import {
  NotificationStoreApi,
  NotificationType,
  notifyCustom,
  prettyLog,
  toast
} from './Notifications';
import type {
  ButtonProps,
  LanguageProperty,
  VoidCallback
} from '../components';
import { Button, Details, HBox, VBox } from '../components';
import { intl } from './format/Language';
import type { FailureResponse } from './Ajax';

export enum ErrorType {
  SCRIPT = 'SCRIPT',
  HTTP = 'HTTP',
  CODED = 'CODED',
  HTTP_CANCELLED = 'HTTP_CANCELLED'
}

export interface HttpError {
  type: ErrorType.HTTP;
  code: string;
  title: string;
  msg: string;
  origin?: string;
}

export interface HttpCancelledError {
  type: ErrorType.HTTP_CANCELLED;
  msg?: string;
}

export interface CodedError {
  type: ErrorType.CODED;
  uniqueCorrelationId: string | undefined;
  code: string;
  title: string;
  msg: string;
  origin?: string;
}

export interface ScriptError {
  type: ErrorType.SCRIPT;
  title: 'Javascript Error' | string;
  origin?: string;
  msg: string;
}

export type CustomError =
  | HttpError
  | HttpCancelledError
  | CodedError
  | ScriptError;

export function instanceofCustomError(obj: unknown): obj is CustomError {
  return typeof obj === 'object' && obj !== null && 'type' in obj;
}

export function instanceofHttpCancelledError(
  obj: unknown
): obj is HttpCancelledError {
  return instanceofCustomError(obj) && obj.type === ErrorType.HTTP_CANCELLED;
}

export function instanceofHttpAbortedError(obj: unknown) {
  return instanceofCustomError(obj) && 'code' in obj && obj.code === 'HTTP0';
}

export function instanceofHttpNetworkError(obj: unknown) {
  return (
    instanceofCustomError(obj) && 'code' in obj && obj.code === 'networkerr'
  );
}

export function instanceofCodedError(obj: unknown): obj is CodedError {
  return instanceofCustomError(obj) && obj.type === ErrorType.CODED;
}

function instanceofFailureResponse(obj: unknown): obj is FailureResponse {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'success' in obj &&
    'error' in obj
  );
}

/**
 * Function to extract a CodedError from a failure response (possibly returned by an ajax response).
 * @param obj the failure response
 * @return the CodedError object or undefined
 */
export function getErrorCodedException(obj: unknown): CodedError | undefined {
  const failureResponse = unwrapFailureResponse(obj);
  if (instanceofCodedError(failureResponse)) {
    return failureResponse;
  }
  return undefined;
}

export function unwrapFailureResponse(obj: unknown): unknown {
  return instanceofFailureResponse(obj) ? obj.error : obj;
}

function getLocalizedErrorMessageTitle(error: CodedError | HttpError): string {
  return intl(
    `error.${error.type}.${error.code}.title` as LanguageProperty,
    error.title
  );
}

export function getLocalizedErrorMessageBody(
  error: CodedError | HttpError
): string {
  return intl(`error.${error.type}.${error.code}.msg` as LanguageProperty);
}

export type DialogButtonOptions = Pick<
  ButtonProps,
  'variant' | 'onClick' | 'text'
>;

export type DialogOptions = {
  buttons?: DialogButtonOptions[];
  hideErrorDetails?: boolean;
};

/**
 * Function to show error message boxes for our custom errors or javascript exceptions.
 *
 * Very convenient to use, e.g.: `await requestPizzas().catch(showError)`.
 * The chained catch makes the expression return SuccessResponse<T> | undefined, whereas normally the return value would be SuccessResponse<T> or throw.
 *
 * @param error The error to show: can be one of our own objects, or a js exception.
 * @param dialogOptions Options for the error dialog.
 * @return undefined to make this usable in a .catch
 */
export function showError(
  error: unknown | FailureResponse | CustomError,
  dialogOptions?: DialogOptions,
  closeFunction?: VoidCallback
): undefined {
  let errObj: CustomError;

  error = unwrapFailureResponse(error);
  if (instanceofCustomError(error)) {
    errObj = error;
  } else {
    errObj = toCustomError(error);
  }

  // If this is a cancelled AJAX Request, we don't want to show an error message.
  if (instanceofHttpCancelledError(errObj)) {
    console.debug('AJAX request cancelled with message', errObj.msg);
    return;
  }

  if (instanceofHttpAbortedError(errObj)) {
    console.debug('AJAX request aborted with message', errObj.msg);
    return;
  }

  if (instanceofHttpNetworkError(errObj)) {
    console.debug(
      'AJAX request failed due to network error with message',
      errObj.msg
    );
    return;
  }

  const finalErr: Exclude<CustomError, HttpCancelledError> = errObj;
  const { uniqueCorrelationId, code } = getLocalizedError(finalErr);

  prettyLog({
    title: finalErr.title,
    message: finalErr.msg,
    type: NotificationType.ERROR,
    code: code,
    uniqueCorrelationId: uniqueCorrelationId
  });

  if (
    NotificationStoreApi.getState().config.notificationType[
      NotificationType.ERROR
    ] === 'toast'
  ) {
    toast(finalErr.msg, NotificationType.ERROR, undefined);
    return;
  }

  notifyCustom(closingCallback => {
    return {
      closeOnOverlayClicked: false,
      type: NotificationType.ERROR,
      title: finalErr.title,
      content: (
        <VBox className="ehs-error-notification">
          {(code || uniqueCorrelationId) && (
            <HBox className="react-error-info" align="space-between">
              <span>{code}</span>
              <span>{uniqueCorrelationId}</span>
            </HBox>
          )}
          <span
            role="alert"
            style={{ padding: '4px 0', margin: '20px 0 10px 0' }}
          >
            {finalErr.msg}
          </span>
          {getErrorDetails(dialogOptions, finalErr.origin)}
        </VBox>
      ),
      actions: <>{getButtons(dialogOptions, closingCallback, closeFunction)}</>
    };
  });

  // Return undefined so .catch(showError) makes an ajax request return T | undefined instead of just the response
  return undefined;
}

/**
 * If an error is not of CustomError type, we transform it to {@link CustomError} with type {@link ErrorType.SCRIPT} and base error title
 * @param error
 */
export function toCustomError(error: unknown): CustomError {
  const javascriptErrorTitle = intl('error.CODED.BASE0000.title');

  if (error instanceof Error) {
    return {
      type: ErrorType.SCRIPT,
      title: javascriptErrorTitle,
      msg: `${error.name}: ${error.message}`
    };
  } else {
    return {
      type: ErrorType.SCRIPT,
      title: javascriptErrorTitle,
      msg: error as string
    };
  }
}

/**
 * In case the error is of {@link ErrorType.CODED} or {@link ErrorType.HTTP} - define title, body and return uniqueCorrelationId and code if such exist
 * @param finalErr
 */
function getLocalizedError(finalErr: Exclude<CustomError, HttpCancelledError>) {
  let uniqueCorrelationId: string | undefined;
  let code: string | undefined;

  // Translate title and message based on type and code
  if (finalErr.type === ErrorType.CODED || finalErr.type === ErrorType.HTTP) {
    finalErr.title = getLocalizedErrorMessageTitle(finalErr);
    finalErr.msg = getLocalizedErrorMessageBody(finalErr);

    // Yes, I know that this is not always set. It can be undefined, that's fine.
    if (instanceofCodedError(finalErr))
      uniqueCorrelationId =
        intl('view.clearingticket.ticketID') +
        ': ' +
        finalErr.uniqueCorrelationId;

    if (finalErr.code) {
      code = intl('common.code') + `: ${finalErr.code}`;
    }
  }
  return {
    uniqueCorrelationId,
    code
  };
}

/**
 * Get buttons array basing on dialog options. If no buttons are in dialog options, a default one is returned
 * @param dialogOptions
 * @param closingCallback
 */
function getButtons(
  dialogOptions: DialogOptions | undefined,
  closingCallback: (arg: VoidCallback | undefined) => VoidCallback,
  closeFunction?: VoidCallback
) {
  const buttons = [];

  if (dialogOptions?.buttons) {
    for (let i = 0; i < dialogOptions.buttons.length; i++) {
      const b = dialogOptions.buttons[i];
      buttons.push(
        <Button
          key={'error-notification-button-' + i}
          variant={b.variant}
          text={b.text}
          onClick={() => {
            if (b.onClick) {
              b.onClick();
            }
            closingCallback(closeFunction)();
          }}
        />
      );
    }
  } else {
    buttons.push(
      <Button
        key="error-notification-button-default"
        variant="primary"
        text={intl('view.button.ok', 'Ok')}
        onClick={closingCallback(closeFunction)}
      />
    );
  }
  return buttons;
}

/**
 * Get error details component
 * @param dialogOptions
 * @param origin
 */
function getErrorDetails(
  dialogOptions: DialogOptions | undefined,
  origin: string | undefined
): JSX.Element {
  const showErrorDetails =
    !dialogOptions ||
    dialogOptions.hideErrorDetails === undefined ||
    !dialogOptions.hideErrorDetails;

  if (origin && showErrorDetails) {
    return (
      <Details>
        <span className="error-origin">{origin}</span>
      </Details>
    );
  }

  return <></>;
}
